import api from "@/utils/api"

// ── Settings ─────────────────────────────────────────────────────────────────

export const rrGetCategories = () =>
    api.req({ url: "/rr/categories", method: api.Method.GET })

export const rrCreateCategory = (body: { name: string; colour?: string; display_order?: number; description?: string }) =>
    api.req({ url: "/rr/categories", method: api.Method.POST, data: body })

export const rrUpdateCategory = (id: number, body: { name?: string; colour?: string; display_order?: number; is_active?: boolean }) =>
    api.req({ url: `/rr/categories/${id}`, method: api.Method.PUT, data: body })

export const rrDeleteCategory = (id: number) =>
    api.req({ url: `/rr/categories/${id}`, method: api.Method.DELETE })

export const rrGetCategoryProducts = (catId: number) =>
    api.req({ url: `/rr/categories/${catId}/products`, method: api.Method.GET })

export const rrToggleProductRR = (catId: number, productId: number) =>
    api.req({ url: `/rr/categories/${catId}/products/${productId}/toggle`, method: api.Method.PATCH })

export const rrGetProductTree = () =>
    api.req({ url: "/rr/product-tree", method: api.Method.GET })

export const rrGetProductSequences = (productId: number) =>
    api.req({ url: `/rr/products/${productId}/sequences`, method: api.Method.GET })

export const rrAddAgentsToSequence = (productId: number, userIds: number[]) =>
    api.req({ url: `/rr/products/${productId}/sequences`, method: api.Method.POST, data: { user_ids: userIds } })

export const rrRemoveAgentFromSequence = (productId: number, userId: number) =>
    api.req({ url: `/rr/products/${productId}/sequences/${userId}`, method: api.Method.DELETE })

export const rrReorderSequence = (productId: number, orderedUserIds: number[]) =>
    api.req({ url: `/rr/products/${productId}/sequences/reorder`, method: api.Method.PUT, data: { ordered_user_ids: orderedUserIds } })

export const rrToggleAgentActive = (productId: number, userId: number) =>
    api.req({ url: `/rr/products/${productId}/sequences/${userId}/toggle`, method: api.Method.PATCH })

export const rrGetAgents = (productId?: number) => {
    const qs = productId ? `?product_id=${productId}` : ""
    return api.req({ url: `/rr/agents${qs}`, method: api.Method.GET })
}

export const rrGetCaps = () =>
    api.req({ url: "/rr/caps", method: api.Method.GET })

export const rrUpdateCaps = (caps: { user_id: number; bucket: string; daily_cap: number; new_lead_cap: number; not_connected_cap: number }[]) =>
    api.req({ url: "/rr/caps", method: api.Method.PUT, data: { caps } })

/** Who changed which cap, when, from what to what (newest first). */
export const rrGetCapsHistory = (userId?: number, limit = 30) =>
    api.req({ url: `/rr/caps/history?limit=${limit}${userId ? `&user_id=${userId}` : ''}`, method: api.Method.GET })

export const rrGetWorkingHours = () =>
    api.req({ url: "/rr/working-hours", method: api.Method.GET })

export const rrUpdateWorkingHours = (hours: { bucket: string; opens: string; closes: string; is_enabled: boolean }[]) =>
    api.req({ url: "/rr/working-hours", method: api.Method.PUT, data: { hours } })

export const rrGetPriorityThresholds = () =>
    api.req({ url: "/rr/priority-thresholds", method: api.Method.GET })

export const rrUpdatePriorityThresholds = (thresholds: { bucket: string; very_high_threshold: number; high_threshold: number; medium_threshold: number }[]) =>
    api.req({ url: "/rr/priority-thresholds", method: api.Method.PUT, data: { thresholds } })

// ── Attendance ────────────────────────────────────────────────────────────────

export const rrPunch = (reason: "manual" | "inactivity" | "auto_shift_end" = "manual") =>
    api.req({ url: `/rr/attendance/punch?reason=${reason}`, method: api.Method.POST })

// Idempotent auto punch-in on login (never punches out)
export const rrEnsurePunchedIn = () =>
    api.req({ url: "/rr/attendance/ensure-in", method: api.Method.POST })

// Presence heartbeat — refreshes last_seen_at while punched in
export const rrHeartbeat = () =>
    api.req({ url: "/rr/attendance/heartbeat", method: api.Method.POST })

export const rrBreak = () =>
    api.req({ url: "/rr/attendance/break", method: api.Method.POST })

export const rrGetAttendanceToday = () =>
    api.req({ url: "/rr/attendance/today", method: api.Method.GET })

export const rrGetAttendanceWeek = () =>
    api.req({ url: "/rr/attendance/week", method: api.Method.GET })

export const rrGetAdminAttendance = (date?: string) => {
    const qs = date ? `?date=${date}` : ""
    return api.req({ url: `/rr/attendance/admin${qs}`, method: api.Method.GET })
}

export const rrGetPunchLogs = (date?: string, userId?: number) => {
    const params = new URLSearchParams()
    if (date) params.set("date", date)
    if (userId) params.set("user_id", String(userId))
    const qs = params.toString() ? `?${params.toString()}` : ""
    return api.req({ url: `/rr/attendance/logs${qs}`, method: api.Method.GET })
}

// ── Dashboard ─────────────────────────────────────────────────────────────────

export const rrGetQueue = (params?: { bucket?: string; page?: number; limit?: number }) => {
    const qs = new URLSearchParams()
    if (params?.bucket) qs.set("bucket", params.bucket)
    if (params?.page)   qs.set("page", String(params.page))
    if (params?.limit)  qs.set("limit", String(params.limit))
    const q = qs.toString()
    return api.req({ url: `/rr/queue${q ? `?${q}` : ""}`, method: api.Method.GET })
}

export const rrAssignFromQueue = (queueId: number, userId: number) =>
    api.req({ url: `/rr/queue/${queueId}/assign`, method: api.Method.POST, data: { user_id: userId } })

export const rrGetWorkload = () =>
    api.req({ url: "/rr/workload", method: api.Method.GET })

export const rrGetMonitor = () =>
    api.req({ url: "/rr/monitor", method: api.Method.GET })

export const rrMovePointer = (productId: number, targetUserId: number) =>
    api.req({ url: `/rr/monitor/${productId}/move-pointer`, method: api.Method.POST, data: { target_user_id: targetUserId } })

export const rrGetHistory = (params?: {
    bucket?: string; assignment_type?: string;
    date_from?: string; date_to?: string;
    search?: string; page?: number; limit?: number
}) => {
    const qs = new URLSearchParams()
    if (params?.bucket)          qs.set("bucket", params.bucket)
    if (params?.assignment_type) qs.set("assignment_type", params.assignment_type)
    if (params?.date_from)       qs.set("date_from", params.date_from)
    if (params?.date_to)         qs.set("date_to", params.date_to)
    if (params?.search)          qs.set("search", params.search)
    if (params?.page)            qs.set("page", String(params.page))
    if (params?.limit)           qs.set("limit", String(params.limit))
    const q = qs.toString()
    return api.req({ url: `/rr/history${q ? `?${q}` : ""}`, method: api.Method.GET })
}
